import { Slot, SlotKind, SlotOccurrence, SlotPlayType } from '@radioking/planning';
import moment from 'moment-timezone';

export interface SlotOccurenceApi {
  idoccurrence: number;
  start_date: string;
  end_date: string;
}

export interface SlotApi {
  idschedule: number;
  idprogram: number;
  idplaylist: number;
  play_type: SlotPlayType;
  schedule_start: string;
  schedule_end: string;
  repeat_end: string;
  repeat_occurrences: number;
  repeat_interval: number;
  repeat_daysofweek: string;
  force_playtime: number;
  tolerance: number;
  jingles_number: number;
  jingles_interval: number;
  jingles_idplaylist?: number;
  jingles_idtrack?: number;
  name: string;
  color: string;
  nboccurrences: number;
  occurrences: SlotOccurenceApi[];
}

export interface AddedSlotApi {
  days?: object;
  end_hour: string;
  force_playtime: number;
  idprogram: number;
  idplaylist: number;
  jingles_idplaylist?: number;
  jingles_idtrack: number;
  jingles_interval: number;
  jingles_number: number;
  jingles_type: string;
  nb_playlists: number;
  play_type: SlotPlayType;
  repeat_daysofweek: string;
  repeat_end: string;
  repeat_interval: number;
  repeat_occurrences: number;
  schedule_end: string;
  schedule_start: string;
  start_hour: string;
  tolerance: number;
}

export interface DroppedSlotApi {
  force_playtime: number;
  idprogram?: number;
  idplaylist?: number;
  nboccurrences: number;
  play_type: SlotPlayType;
  repeat_interval: number;
  schedule_end: string;
  schedule_start: string;
  tolerance: number;
}

export interface PlanningApi {
  status: string;
  data: SlotApi[];
}

export function convertToSlot(slotApi: SlotApi, offset: number): Slot {
  return {
    id: slotApi.idschedule,
    program: slotApi?.idprogram,
    playlist: slotApi?.idplaylist,
    kind: slotApi?.idprogram ? SlotKind.program : SlotKind.playlist,
    playType: slotApi.play_type,
    start: moment(slotApi.schedule_start),
    end: moment(slotApi.schedule_end),
    fixedTime: !!slotApi.force_playtime,
    tolerance: slotApi.tolerance,
    inlay:
      slotApi.jingles_idplaylist !== null || slotApi.jingles_idtrack !== null
        ? {
            type: slotApi.jingles_idtrack ? 'track' : 'playlist',
            id: slotApi.jingles_idtrack
              ? slotApi.jingles_idtrack
              : slotApi.jingles_idplaylist,
            interval: slotApi.jingles_interval,
            playlistTracks: slotApi.jingles_number,
          }
        : null,
    repeat:
      slotApi.repeat_interval !== 0
        ? {
            repeatCount: slotApi.repeat_occurrences,
            repeatDays: slotApi.repeat_daysofweek,
            repeatEnd: moment(slotApi.repeat_end),
            repeatInterval: slotApi.repeat_interval,
          }
        : null,
    occurrences: slotApi.occurrences?.map(o => {
      return {
        id: o.idoccurrence,
        startDate: moment(o.start_date).utcOffset(offset),
        endDate: moment(o.end_date).utcOffset(offset),
      };
    }),
  };
}

export function convertToAddedSlotApi(slot: Slot): AddedSlotApi {
  return {
    end_hour: slot.endHour.tz('UTC').format(),
    force_playtime: slot.fixedTime ? 1 : 0,
    idprogram: slot.kind === 'program' ? slot.program : null,
    idplaylist: slot.kind === 'playlist' ? slot.playlist : null,
    jingles_idplaylist: slot.inlay?.type === 'playlist' ? slot.inlay.id : null,
    jingles_idtrack: slot.inlay?.type === 'track' ? slot.inlay.id : null,
    jingles_interval: slot.inlay?.interval,
    jingles_number: slot.inlay?.type === 'playlist' ? slot.inlay?.playlistTracks : 1,
    jingles_type: slot.inlay?.type,
    nb_playlists: 1,
    play_type: slot.playType,
    repeat_daysofweek: slot.repeat?.repeatDays || '',
    repeat_end: slot.repeat?.repeatEnd?.tz('UTC').format() || null,
    repeat_interval: slot.repeat?.repeatInterval || 0,
    repeat_occurrences: slot.repeat?.repeatCount || null,
    schedule_end: slot.end.tz('UTC').format(),
    schedule_start: slot.start.tz('UTC').format(),
    start_hour: slot.startHour.tz('UTC').format(),
    tolerance: slot.tolerance,
  };
}

export function convertToDroppedSlotApi(slot: Partial<Slot>): DroppedSlotApi {
  const slotApi: DroppedSlotApi = {
    force_playtime: slot.fixedTime ? 1 : 0,
    nboccurrences: 1,
    play_type: 'sequential',
    tolerance: 5,
    repeat_interval: 0,
    schedule_start: slot.start.tz('UTC').format(),
    schedule_end: slot.end.tz('UTC').format(),
  };
  if (slot.kind === 'program') {
    slotApi.idprogram = slot.program;
  } else {
    slotApi.idplaylist = slot.playlist;
  }
  return slotApi;
}

export function convertSlotToPlanningDatesApi(slot: Slot): Partial<AddedSlotApi> {
  return {
    schedule_start: slot.start.tz('UTC').format(),
    schedule_end: slot.end.tz('UTC').format(),
  };
}

export function convertOccurrenceToPlanningDatesApi(
  occ: SlotOccurrence,
): Partial<SlotOccurenceApi> {
  return {
    start_date: occ.startDate.tz('UTC').format(),
    end_date: occ.endDate.tz('UTC').format(),
  };
}
